import { Trans } from 'react-i18next';

import { Button, Flex } from 'antd';

import { store } from 'app/appStore';
import ErrorIcon from 'assets/images/notification/error.svg?react';
import InfoIcon from 'assets/images/notification/info.svg?react';
import SuccessIcon from 'assets/images/notification/success.svg?react';
import WaitIcon from 'assets/images/notification/wait.svg?react';
import { EActionReportStatuses, EReportTypes } from 'entities/Report';
import { ETransactionTypes } from 'entities/Transaction';
import { EFilters } from 'features/Filters';
import { i18n } from 'i18n';
import { EBrokenLinksErrors, EMainRoutes } from 'shared/typings';
import { Link } from 'shared/ui';

import { ENotificationTypes, IChangedTariff, IExtra, INotificationType } from '../model/types';

export const NOTIFICATIONS_TYPES = (): Record<ENotificationTypes, INotificationType> => ({
	[ENotificationTypes.SOURCES_ACTIVE]: {
		text: (_, __, source) => {
			return (
				<Trans i18nKey="notificationDescSourcesActive">
					Ваша площадка <Link to={`/${EMainRoutes.SOURCES}/`}>{{ source: source?.name || '' }}</Link> успешно прошла
					модерацию. Теперь вы можете подать заявку на оффер в разделе Каталог
				</Trans>
			);
		},
		title: (_, __, source) => (
			<Trans i18nKey="notificationTitleSourcesActive">Площадка {{ source: source?.name || '' }} прошла модерацию</Trans>
		),
		link: () => (
			<Trans i18nKey="notificationLinkSourcesActive">
				<Link to={`/${EMainRoutes.CATALOG}/`}>Перейти в Каталог</Link>
			</Trans>
		),
		icon: <SuccessIcon />,
		extra: [''],
	},

	[ENotificationTypes.SOURCES_REJECTED]: {
		text: (_, __, source) => {
			return (
				<Trans i18nKey="notificationDescRejected">
					К сожалению, площадка <Link to={`/${EMainRoutes.SOURCES}/`}>{{ source: source?.name || '' }}</Link> не прошла
					модерацию. Ознакомьтесь с причинами отклонения в разделе "Мои площадки" и внесите необходимые изменения.
				</Trans>
			);
		},
		title: (_, __, source) => (
			<Trans i18nKey="notificationTitleRejected">Площадка {{ source: source?.name || '' }} отклонена</Trans>
		),
		link: () => (
			<Trans i18nKey="notificationLinkRejected">
				<Link to={`/${EMainRoutes.SOURCES}/`}>Перейти в Мои площадки</Link>
			</Trans>
		),
		icon: <ErrorIcon />,
		extra: [''],
	},

	[ENotificationTypes.SOURCES_NOT_VERIFIED]: {
		text: (_, __, source) => {
			return (
				<Trans i18nKey="notificationDescNotVerified">
					К сожалению, ваша площадка <Link to={`/${EMainRoutes.SOURCES}/`}>{{ source: source?.name || '' }}</Link> не
					прошла верификацию владения. Проверьте корректность предоставленных данных и попробуйте снова.
				</Trans>
			);
		},
		title: (_, __, source) => (
			<Trans i18nKey="notificationTitleNotVerified">
				Площадка {{ source: source?.name || '' }}: верификация владения не пройдена
			</Trans>
		),
		link: () => (
			<Trans i18nKey="notificationLinkNotVerified">
				<Link to={`/${EMainRoutes.SOURCES}/`}>Перейти в Мои площадки</Link>
			</Trans>
		),
		icon: <ErrorIcon />,
		extra: [''],
	},

	[ENotificationTypes.SOURCES_ON_VERIFICATION]: {
		text: (_, __, source) => {
			return (
				<Trans i18nKey="notificationDescOnVerification">
					Площадка <Link to={`/${EMainRoutes.SOURCES}/`}>{{ source: source?.name || '' }}</Link> успешно отправлена на
					верификацию владения. Мы уведомим вас, как только процесс будет завершен.
				</Trans>
			);
		},

		title: (_, __, source) => (
			<Trans i18nKey="notificationTitleOnVerification">Площадка {{ source: source?.name || '' }} на верификации</Trans>
		),
		link: () => (
			<Trans i18nKey="notificationLinkOnVerification">
				<Link to={`/${EMainRoutes.SOURCES}/`}>Перейти в Мои площадки</Link>
			</Trans>
		),
		icon: <WaitIcon />,
		extra: [''],
	},

	[ENotificationTypes.SOURCES_ON_MODERATION]: {
		text: (_, __, source) => {
			return (
				<Trans i18nKey="notificationDescOnModeration">
					Площадка <Link to={`/${EMainRoutes.SOURCES}/`}>{{ source: source?.name || '' }}</Link> успешно прошла
					верификацию владения и передана на модерацию. Мы сообщим результаты модерации в ближайшее время.
				</Trans>
			);
		},
		title: (_, __, source) => (
			<Trans i18nKey="notificationTitleOnModeration">
				Площадка {{ source: source?.name || '' }} отправлена на модерацию
			</Trans>
		),
		link: () => (
			<Trans i18nKey="notificationLinkOnModeration">
				<Link to={`/${EMainRoutes.SOURCES}/`}>Перейти в Мои площадки</Link>
			</Trans>
		),
		icon: <WaitIcon />,
		extra: [''],
	},

	[ENotificationTypes.SOURCE_BRAND_CREATED]: {
		text: (brand, _, source) => {
			return (
				<Trans i18nKey="notificationDescJoinBrandCreated">
					Ваша заявка на бренд{' '}
					<Link to={`/${EMainRoutes.CATALOG}/?${EFilters.BRAND}=${brand?.uuid}`}>{{ brand: brand?.name || '' }}</Link>{' '}
					получена. Предоставленная площадка{' '}
					<Link to={`/${EMainRoutes.SOURCES}/`}>{{ source: source?.name || '' }}</Link> находится на модерации для этого
					бренда.
				</Trans>
			);
		},
		title: (brand) => (
			<Trans i18nKey="notificationTitleJoinBrandCreated">Заявка на бренд {{ brand: brand?.name || '' }} получена</Trans>
		),
		icon: <WaitIcon />,
		extra: [''],
	},

	[ENotificationTypes.SOURCE_OFFER_CREATED]: {
		text: (_, offer, source) => {
			return (
				<Trans i18nKey="notificationDescSourceBrandCreated">
					Ваша заявка на оффер <Link to={`/${EMainRoutes.MY_OFFERS}/`}>{{ offer: offer?.name || '' }}</Link> получена.
					Предоставленная площадка <Link to={`/${EMainRoutes.SOURCES}/`}>{{ source: source?.name || '' }}</Link>{' '}
					находится на модерации для этого оффера.
				</Trans>
			);
		},
		title: (_, offer) => (
			<Trans i18nKey="notificationTitleSourceBrandCreated">
				Заявка на оффер {{ offer: offer?.name || '' }} получена
			</Trans>
		),
		icon: <WaitIcon />,
		extra: [''],
	},

	[ENotificationTypes.SOURCE_OFFER_JOINED]: {
		text: (_, offer, source) => {
			return (
				<Trans i18nKey="notificationDescJoinOfferJoined">
					Поздравляем! Площадка <Link to={`/${EMainRoutes.SOURCES}/`}>{{ source: source?.name || '' }}</Link> успешно
					подключена к офферу <Link to={`/${EMainRoutes.MY_OFFERS}/`}>{{ offer: offer?.name || '' }}</Link>
				</Trans>
			);
		},
		title: (_, __, source) => (
			<Trans i18nKey="notificationTitleJoinOfferJoined">Площадка {{ source: source?.name || '' }} подключена</Trans>
		),
		link: () => (
			<Trans i18nKey="notificationLinkOnModeration">
				<Link to={`/${EMainRoutes.SOURCES}/`}>Перейти в Мои площадки</Link>
			</Trans>
		),
		icon: <SuccessIcon />,
		extra: [''],
	},

	[ENotificationTypes.SOURCE_OFFER_REJECTED]: {
		text: (_, offer, source, extra = ['']) => {
			const [rejection_comment] = extra;

			return (
				<Trans i18nKey="notificationDescJoinJoinOfferRejected">
					Заявка на подключение площадки <Link to={`/${EMainRoutes.SOURCES}/`}>{{ source: source?.name || '' }}</Link>{' '}
					на оффера <Link to={`/${EMainRoutes.MY_OFFERS}/`}>{{ offer: offer?.name || '' }}</Link> была отклонена.
					Комментарий: {{ rejection_comment }} Пожалуйста, ознакомьтесь с причиной отказа и при необходимости
					скорректируйте вашу площадку.
				</Trans>
			);
		},
		title: (_, __, source) => (
			<Trans i18nKey="notificationTitleJoinOfferRejected">Площадка {{ source: source?.name || '' }} отклонена</Trans>
		),
		link: () => (
			<Trans i18nKey="notificationLinkOnModeration">
				<Link to={`/${EMainRoutes.SOURCES}/`}>Перейти в Мои площадки</Link>
			</Trans>
		),
		icon: <ErrorIcon />,
		extra: ['rejection_comment'],
	},

	[ENotificationTypes.SOURCE_OFFER_STOPPED]: {
		text: (_, offer, source, extra = ['']) => {
			const [rejection_comment] = extra;

			return (
				<Trans i18nKey="notificationDescJoinOfferStopped">
					Подключение площадки <Link to={`/${EMainRoutes.SOURCES}/`}>{{ source: source?.name || '' }}</Link> к офферу{' '}
					<Link to={`/${EMainRoutes.MY_OFFERS}/`}>{{ offer: offer?.name || '' }}</Link> было прекращено. Комментарий:{' '}
					{{ rejection_comment }}
				</Trans>
			);
		},
		title: (_, __, source) => (
			<Trans i18nKey="notificationTitleJoinOfferStopped">
				Подключение площадки {{ source: source?.name || '' }} остановлено
			</Trans>
		),
		link: () => (
			<Trans i18nKey="notificationLinkOnModeration">
				<Link to={`/${EMainRoutes.SOURCES}/`}>Перейти в Мои площадки</Link>
			</Trans>
		),
		icon: <ErrorIcon />,
		extra: ['rejection_comment'],
	},

	[ENotificationTypes.OFFER_INVITE]: {
		text: (_, offer) => {
			return (
				<Trans i18nKey="notificationDescOfferInvite">
					Вам предварительно одобрено подключение к {{ offer: offer?.name || '' }}
				</Trans>
			);
		},
		title: (_, offer) => (
			<Trans i18nKey="notificationTitleOfferInvite">Предлагаем подключить {{ offer: offer?.name || '' }}</Trans>
		),
		link: (_, __, ___, actions, isLoading) => {
			if (!actions) {
				return null;
			}

			const [onCancel, onApply] = actions;

			return (
				<Trans i18nKey="notificationLinkOfferInvite">
					<Flex gap={12}>
						<Button onClick={onCancel} loading={isLoading} disabled={isLoading} size="small">
							Отклонить
						</Button>
						<Button onClick={onApply} size="small" type="primary">
							Принять
						</Button>
					</Flex>
				</Trans>
			);
		},
		icon: <InfoIcon />,
		extra: [''],
	},

	[ENotificationTypes.BRAND_INVITE]: {
		text: (brand) => {
			return (
				<Trans i18nKey="notificationDescBrandInvite">
					Вам предварительно одобрено подключение к {{ brand: brand?.name || '' }}
				</Trans>
			);
		},
		title: (brand) => (
			<Trans i18nKey="notificationTitleBrandInvite">Предлагаем подключить бренд {{ brand: brand?.name || '' }}</Trans>
		),
		link: (_, __, ___, actions, isLoading) => {
			if (!actions) {
				return null;
			}

			const [onCancel, onApply] = actions;

			return (
				<Trans i18nKey="notificationLinkBrandInvite">
					<Flex gap={12}>
						<Button onClick={onCancel} loading={isLoading} disabled={isLoading} size="small">
							Отклонить
						</Button>
						<Button onClick={onApply} size="small" type="primary">
							Принять
						</Button>
					</Flex>
				</Trans>
			);
		},
		icon: <InfoIcon />,
		extra: [''],
	},

	[ENotificationTypes.NEW_OFFER]: {
		text: (brand, offer) => {
			return (
				<Trans i18nKey="notificationDescNewOffer">
					У бренда {{ brand: brand?.name || '' }} добавлен новый оффер {{ offer: offer?.name || '' }}
				</Trans>
			);
		},
		title: (_, offer) => <Trans i18nKey="notificationTitleNewOffer">Новый оффер {{ offer: offer?.name || '' }}</Trans>,
		link: (_, __, ___, actions) => {
			if (!actions) {
				return null;
			}

			const [onMore, onApply] = actions;

			return (
				<Flex gap={12}>
					<Button onClick={onMore} size="small">
						{i18n.t('Подробнее')}
					</Button>
					<Button onClick={onApply} size="small" type="primary">
						{i18n.t('Подключиться')}
					</Button>
				</Flex>
			);
		},
		icon: <InfoIcon />,
		extra: [''],
	},

	[ENotificationTypes.RATE_CHANGES]: {
		text: (_, offer, __, extra = ['']) => {
			const changes = (extra[0] as IChangedTariff[]) || [];

			return (
				<>
					<Trans i18nKey="notificationDescRateChanges">
						Ставка на оффере{' '}
						<Link to={`/${EMainRoutes.CATALOG}/${EMainRoutes.OFFER}/${offer?.uuid}`}>
							{{ offer: offer?.name || '' }}
						</Link>{' '}
						была обновлена. Проверьте текущие условия, чтобы быть в курсе актуальных изменений.
						<span style={{ opacity: 0, visibility: 'hidden' }}>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
					</Trans>
					<Flex vertical gap={8} style={{ padding: '8px 0' }} className="extra_changes">
						{changes.map((change) => {
							return (
								<Flex vertical gap={4}>
									<span>{change.tariff.name}</span>
									<ul style={{ margin: 0 }}>
										{change.sources.map((source) => {
											return (
												<li>
													{source.source ? source.source.name : i18n.t('Общая ставка')}:{' '}
													{source.previous_payout ? `${source.previous_payout} → ` : ''}
													{source.current_payout}
												</li>
											);
										})}
									</ul>
								</Flex>
							);
						})}
					</Flex>
				</>
			);
		},
		title: (_, offer) => (
			<Trans i18nKey="notificationTitleRateChanges">Ставка оффера {{ offer: offer?.name || '' }} обновлена</Trans>
		),
		icon: <InfoIcon />,
		extra: ['changes'],
	},

	[ENotificationTypes.NEW_OFFERS]: {
		text: (_, __, ___, extra = ['']) => {
			const changes = (extra[0] as IExtra) || [];

			return (
				<>
					<Trans i18nKey="notificationDescNewOffers">На этой неделе добавлены:</Trans>
					<div className="new_offers">
						<ul style={{ margin: 0 }}>
							{Object.keys(changes).map((key) => {
								const change = changes[key as keyof typeof changes];
								return (
									<li>
										{typeof change === 'string' && (
											<Link to={`/${EMainRoutes.CATALOG}/${EMainRoutes.OFFER}/${key}/`}>{change}</Link>
										)}
									</li>
								);
							})}
						</ul>
					</div>
				</>
			);
		},
		title: () => <Trans i18nKey="notificationTitleNewOffers">Новые бренды и офферы за неделю!</Trans>,
		icon: <InfoIcon />,
		extra: ['all'],
	},

	[ENotificationTypes.BALANCE_UPDATE]: {
		text: (_, __, ___, extra = ['', '']) => {
			const [awaiting_payment, ready_for_withdrawal] = extra as string[];

			return (
				<>
					{awaiting_payment && (
						<Trans i18nKey="notificationDescBalanceUpdateAwaiting">
							Сумма в размере {{ sum: awaiting_payment }} подтверждена и ожидает оплаты рекламодателя(см в разделе{' '}
							<Link
								to={`/${EMainRoutes.REPORTS}/?${EFilters.REPORT_TYPE}=${EReportTypes.ACTION}&${EFilters.STATUS}=${EActionReportStatuses.AWAITING_PAYMENT}`}>
								Статистика
							</Link>
							).{' '}
						</Trans>
					)}
					{awaiting_payment && ready_for_withdrawal && <br />}
					{ready_for_withdrawal && (
						<Trans i18nKey="notificationDescBalanceUpdateAprove">
							Сумма в размере {{ sum: ready_for_withdrawal }} уже готова к выводу в разделе{' '}
							<Link to={`/${EMainRoutes.FINANCES}/?${EFilters.TYPE}=${ETransactionTypes.REPLENISHMENT}`}>Финансы</Link>
						</Trans>
					)}
				</>
			);
		},
		title: () => <Trans i18nKey="notificationTitleBalanceUpdate">Обновления по балансу</Trans>,
		icon: <InfoIcon />,
		extra: ['awaiting_payment', 'ready_for_withdrawal'],
	},

	[ENotificationTypes.BONUS_CREATED]: {
		text: (_, offer, __, extra = ['', '']) => {
			const [amount, action_uuid] = extra as string[];

			return (
				<Trans i18nKey="notificationDescBonusCreated">
					Вам начислен бонус в размере {{ sum: amount }} по офферу{' '}
					<Link to={`/${EMainRoutes.CATALOG}/${EMainRoutes.OFFER}/${offer?.uuid}`}>{{ offer: offer?.name || '' }}</Link>
					. Подробности доступны в разделе{' '}
					<Link
						to={`/${EMainRoutes.REPORTS}/?${EFilters.REPORT_TYPE}=${EReportTypes.ACTION}&${EFilters.SEARCH_ID}=${action_uuid}`}>
						статистики
					</Link>
					.
				</Trans>
			);
		},
		title: () => <Trans i18nKey="notificationTitleBonusCreated">Вам начислен бонус</Trans>,
		icon: <InfoIcon />,
		extra: ['amount', 'action_uuid'],
	},

	[ENotificationTypes.TELEGRAM]: {
		text: () => {
			return <Trans i18nKey="notificationDescTelegram">Вы подключили telegram бот</Trans>;
		},
		title: () => <Trans i18nKey="notificationTitleTelegram">Вы подключили telegram бот</Trans>,
		icon: <InfoIcon />,
		extra: [''],
	},

	[ENotificationTypes.BROKEN_LINK]: {
		text: (_, offer, source, extra = ['']) => {
			const [original_url, error_code] = extra;
			const translations = store.getState().commonSlice.trans;

			return (
				<Trans i18nKey="notificationDescBrokenLink">
					Причина: {{ reason: translations.brokenLinksErrors[error_code as EBrokenLinksErrors] }}
					<br />
					Площадка: <Link to={`/${EMainRoutes.SOURCES}/`}>{{ source: source?.name || '' }}</Link>
					<br />
					Оффер: <Link to={`/${EMainRoutes.MY_OFFERS}/`}>{{ offer: offer?.name || '' }}</Link>
					<br />
					Ссылка: {{ link: original_url }}
				</Trans>
			);
		},
		title: (_, __, ___, extra = ['']) => {
			const [original_url] = extra;

			return (
				<Trans i18nKey="notificationTitleBrokenLink">
					Обнаружен переход по{' '}
					<Link to={`/${EMainRoutes.TOOLS}/${EMainRoutes.BROKEN_LINKS}/?search=${original_url}`}>битой ссылке</Link>
				</Trans>
			);
		},

		icon: <ErrorIcon />,
		extra: ['original_url', 'error_code'],
	},

	[ENotificationTypes.CUSTOM]: {
		text: () => '',
		title: () => '',
		extra: [''],
		icon: '',
	},
});
